// ============================================
// ShapeBatch - Primitive shape rendering
// ============================================

import { type IRenderContext } from '../core/IRenderContext';
import { type Camera2D } from '../objects/Camera2D';
import {
  type BufferHandle,
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../types';

// WGSL shader for shapes
const SHAPE_WGSL = `
struct Uniforms {
  viewProjection: mat4x4<f32>,
};

@binding(0) @group(0) var<uniform> uniforms: Uniforms;

struct VertexInput {
  @location(0) position: vec2<f32>,
  @location(1) color: vec4<f32>,
};

struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) color: vec4<f32>,
};

@vertex
fn vs_main(in: VertexInput) -> VertexOutput {
  var out: VertexOutput;
  out.position = uniforms.viewProjection * vec4<f32>(in.position, 0.0, 1.0);
  out.color = in.color;
  return out;
}

@fragment
fn fs_main(in: VertexOutput) -> @location(0) vec4<f32> {
  return in.color;
}
`;

// Vertex format: position (2 floats) + color (4 floats) = 6 floats = 24 bytes
const SHAPE_VERTEX_SIZE = 24;
const SHAPE_VERTEX_LAYOUT = {
  stride: SHAPE_VERTEX_SIZE,
  attributes: [
    { name: 'aPosition', format: 'float32x2' as const, offset: 0, location: 0 },
    { name: 'aColor', format: 'float32x4' as const, offset: 8, location: 1 },
  ],
};

const DEFAULT_MAX_VERTICES = 20000;

/**
 * ShapeBatch - Immediate mode shape rendering
 * For lines, rectangles, circles etc.
 */
export class ShapeBatch {
  private ctx: IRenderContext;
  private maxVertices: number;
  
  // GPU resources - lines (LDR = swap chain, HDR = rgba16float scene targets)
  private vertexBuffer: BufferHandle = NULL_HANDLE;
  private shader: ShaderHandle = NULL_HANDLE;
  private pipeline: PipelineHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;

  // GPU resources - triangles (filled shapes)
  private triangleBuffer: BufferHandle = NULL_HANDLE;
  private trianglePipeline: PipelineHandle = NULL_HANDLE;
  private trianglePipelineHDR: PipelineHandle = NULL_HANDLE;

  // Whether the active batch targets an HDR (rgba16float) buffer — selects the matching pipelines.
  private currentHdrTarget = false;
  
  // Vertex data - lines
  private vertexData: Float32Array;
  private vertexCount = 0;
  
  // Vertex data - triangles
  private triangleData: Float32Array;
  private triangleCount = 0;
  
  // GPU buffer offsets — cumulative vertices written this frame.
  // Prevents WebGPU buffer overwrites when flush() is called mid-batch
  // (all queue.writeBuffer calls execute before the render pass).
  private lineBaseVertex = 0;
  private triBaseVertex = 0;
  
  private batching = false;
  
  // View-projection
  private vpMatrix = new Float32Array(16);
  
  // Current color
  private colorR = 1;
  private colorG = 1;
  private colorB = 1;
  private colorA = 1;
  
  constructor(ctx: IRenderContext, options?: { maxVertices?: number }) {
    this.ctx = ctx;
    this.maxVertices = options?.maxVertices ?? DEFAULT_MAX_VERTICES;
    
    // Allocate vertex data for lines
    this.vertexData = new Float32Array(this.maxVertices * 6);
    // Allocate vertex data for triangles (filled shapes)
    this.triangleData = new Float32Array(this.maxVertices * 6);
    
    this.initResources();
  }
  
  private initResources(): void {
    const { ctx } = this;
    
    // Create shader (WGSL)
    this.shader = ctx.createShader(SHAPE_WGSL, SHAPE_WGSL);
    
    // Create pipelines for lines (LDR swap chain + HDR rgba16float scene target)
    this.pipeline = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [SHAPE_VERTEX_LAYOUT],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      primitive: { topology: 'line-list', usesTextures: false },
      label: 'ShapeBatch-lines',
    });
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [SHAPE_VERTEX_LAYOUT],
      colorTargets: [{ format: 'rgba16float' }],
      primitive: { topology: 'line-list', usesTextures: false },
      label: 'ShapeBatch-lines-hdr',
    });

    // Create pipelines for triangles (filled shapes)
    this.trianglePipeline = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [SHAPE_VERTEX_LAYOUT],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      primitive: { topology: 'triangle-list', usesTextures: false },
      label: 'ShapeBatch-triangles',
    });
    this.trianglePipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [SHAPE_VERTEX_LAYOUT],
      colorTargets: [{ format: 'rgba16float' }],
      primitive: { topology: 'triangle-list', usesTextures: false },
      label: 'ShapeBatch-triangles-hdr',
    });
    
    // Create vertex buffer for lines
    this.vertexBuffer = ctx.createBuffer({
      size: this.maxVertices * SHAPE_VERTEX_SIZE,
      usage: ['vertex'],
      label: 'ShapeBatch-lines-vb',
    });
    
    // Create vertex buffer for triangles
    this.triangleBuffer = ctx.createBuffer({
      size: this.maxVertices * SHAPE_VERTEX_SIZE,
      usage: ['vertex'],
      label: 'ShapeBatch-triangles-vb',
    });
  }
  
  /**
   * Reset GPU buffer cursors to the beginning. Must be called once per
   * frame BEFORE any begin()/end() cycles to reclaim the vertex buffer.
   */
  startFrame(): void {
    this.lineBaseVertex = 0;
    this.triBaseVertex = 0;
  }
  
  /**
   * Begin shape batch
   * @param isHdrTarget true when the bound render target is rgba16float (HDR scene buffer)
   */
  begin(camera: Camera2D, isHdrTarget = false): void {
    if (this.batching) {
      throw new Error('ShapeBatch.begin() called without ending');
    }

    this.batching = true;
    this.currentHdrTarget = isHdrTarget;
    this.vertexCount = 0;
    this.triangleCount = 0;

    camera.getViewProjection(this.vpMatrix);
  }
  
  /**
   * End batch and render
   */
  end(): void {
    if (!this.batching) {
      throw new Error('ShapeBatch.end() called without begin()');
    }
    
    this.flush();
    this.batching = false;
  }
  
  /**
   * Set current color
   */
  setColor(r: number, g: number, b: number, a = 1): void {
    this.colorR = r;
    this.colorG = g;
    this.colorB = b;
    this.colorA = a;
  }
  
  /**
   * Set color from hex
   */
  setColorHex(hex: number, alpha = 1): void {
    this.colorR = ((hex >> 16) & 0xFF) / 255;
    this.colorG = ((hex >> 8) & 0xFF) / 255;
    this.colorB = (hex & 0xFF) / 255;
    this.colorA = alpha;
  }
  
  /**
   * Draw a line
   */
  line(x1: number, y1: number, x2: number, y2: number): void {
    if (this.lineBaseVertex + this.vertexCount + 2 > this.maxVertices) {
      this.flush();
      if (this.lineBaseVertex + this.vertexCount + 2 > this.maxVertices) return;
    }
    
    this.pushVertex(x1, y1);
    this.pushVertex(x2, y2);
  }
  
  /**
   * Draw rectangle outline
   */
  rect(x: number, y: number, width: number, height: number): void {
    const x2 = x + width;
    const y2 = y + height;
    
    this.line(x, y, x2, y);
    this.line(x2, y, x2, y2);
    this.line(x2, y2, x, y2);
    this.line(x, y2, x, y);
  }
  
  /**
   * Draw rotated rectangle outline
   * @param cx Center X
   * @param cy Center Y  
   * @param width Rectangle width
   * @param height Rectangle height
   * @param rotation Rotation in radians
   */
  rectRotated(cx: number, cy: number, width: number, height: number, rotation: number): void {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const hw = width / 2;
    const hh = height / 2;
    
    // Calculate rotated corner positions
    const x0 = cx + (-hw * cos - -hh * sin);
    const y0 = cy + (-hw * sin + -hh * cos);
    const x1 = cx + (hw * cos - -hh * sin);
    const y1 = cy + (hw * sin + -hh * cos);
    const x2 = cx + (hw * cos - hh * sin);
    const y2 = cy + (hw * sin + hh * cos);
    const x3 = cx + (-hw * cos - hh * sin);
    const y3 = cy + (-hw * sin + hh * cos);
    
    this.line(x0, y0, x1, y1);
    this.line(x1, y1, x2, y2);
    this.line(x2, y2, x3, y3);
    this.line(x3, y3, x0, y0);
  }
  
  /**
   * Draw filled rectangle (as two triangles)
   */
  rectFilled(x: number, y: number, width: number, height: number): void {
    if (this.triBaseVertex + this.triangleCount + 6 > this.maxVertices) {
      this.flush();
      if (this.triBaseVertex + this.triangleCount + 6 > this.maxVertices) return;
    }
    
    const x2 = x + width;
    const y2 = y + height;
    
    // Triangle 1: top-left, top-right, bottom-right
    this.pushTriangleVertex(x, y);
    this.pushTriangleVertex(x2, y);
    this.pushTriangleVertex(x2, y2);
    
    // Triangle 2: top-left, bottom-right, bottom-left
    this.pushTriangleVertex(x, y);
    this.pushTriangleVertex(x2, y2);
    this.pushTriangleVertex(x, y2);
  }
  
  /**
   * Draw filled rotated rectangle
   * @param cx Center X
   * @param cy Center Y
   * @param width Rectangle width
   * @param height Rectangle height
   * @param rotation Rotation in radians
   */
  rectFilledRotated(cx: number, cy: number, width: number, height: number, rotation: number): void {
    if (this.triBaseVertex + this.triangleCount + 6 > this.maxVertices) {
      this.flush();
      if (this.triBaseVertex + this.triangleCount + 6 > this.maxVertices) return;
    }
    
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const hw = width / 2;
    const hh = height / 2;
    
    // Calculate rotated corner positions
    const x0 = cx + (-hw * cos - -hh * sin);
    const y0 = cy + (-hw * sin + -hh * cos);
    const x1 = cx + (hw * cos - -hh * sin);
    const y1 = cy + (hw * sin + -hh * cos);
    const x2 = cx + (hw * cos - hh * sin);
    const y2 = cy + (hw * sin + hh * cos);
    const x3 = cx + (-hw * cos - hh * sin);
    const y3 = cy + (-hw * sin + hh * cos);
    
    // Triangle 1
    this.pushTriangleVertex(x0, y0);
    this.pushTriangleVertex(x1, y1);
    this.pushTriangleVertex(x2, y2);
    
    // Triangle 2
    this.pushTriangleVertex(x0, y0);
    this.pushTriangleVertex(x2, y2);
    this.pushTriangleVertex(x3, y3);
  }
  
  /**
   * Draw filled circle
   */
  circleFilled(cx: number, cy: number, radius: number, segments = 32): void {
    if (this.triBaseVertex + this.triangleCount + segments * 3 > this.maxVertices) {
      this.flush();
      if (this.triBaseVertex + this.triangleCount + segments * 3 > this.maxVertices) return;
    }
    
    const step = (Math.PI * 2) / segments;
    
    for (let i = 0; i < segments; i++) {
      const a1 = i * step;
      const a2 = (i + 1) * step;
      
      // Triangle from center to edge
      this.pushTriangleVertex(cx, cy);
      this.pushTriangleVertex(cx + Math.cos(a1) * radius, cy + Math.sin(a1) * radius);
      this.pushTriangleVertex(cx + Math.cos(a2) * radius, cy + Math.sin(a2) * radius);
    }
  }

  /**
   * Draw filled triangle
   */
  triangleFilled(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): void {
    if (this.triBaseVertex + this.triangleCount + 3 > this.maxVertices) {
      this.flush();
      if (this.triBaseVertex + this.triangleCount + 3 > this.maxVertices) return;
    }
    
    this.pushTriangleVertex(x1, y1);
    this.pushTriangleVertex(x2, y2);
    this.pushTriangleVertex(x3, y3);
  }
  
  /**
   * Draw circle outline
   */
  circle(cx: number, cy: number, radius: number, segments = 32): void {
    const step = (Math.PI * 2) / segments;
    
    for (let i = 0; i < segments; i++) {
      const a1 = i * step;
      const a2 = (i + 1) * step;
      
      const x1 = cx + Math.cos(a1) * radius;
      const y1 = cy + Math.sin(a1) * radius;
      const x2 = cx + Math.cos(a2) * radius;
      const y2 = cy + Math.sin(a2) * radius;
      
      this.line(x1, y1, x2, y2);
    }
  }
  
  /**
   * Draw arc
   */
  arc(cx: number, cy: number, radius: number, startAngle: number, endAngle: number, segments = 16): void {
    const step = (endAngle - startAngle) / segments;
    
    for (let i = 0; i < segments; i++) {
      const a1 = startAngle + i * step;
      const a2 = startAngle + (i + 1) * step;
      
      const x1 = cx + Math.cos(a1) * radius;
      const y1 = cy + Math.sin(a1) * radius;
      const x2 = cx + Math.cos(a2) * radius;
      const y2 = cy + Math.sin(a2) * radius;
      
      this.line(x1, y1, x2, y2);
    }
  }
  
  /**
   * Draw polyline
   */
  polyline(points: { x: number; y: number }[], closed = false): void {
    if (points.length < 2) return;
    
    for (let i = 0; i < points.length - 1; i++) {
      this.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
    }
    
    if (closed && points.length > 2) {
      const last = points[points.length - 1];
      const first = points[0];
      this.line(last.x, last.y, first.x, first.y);
    }
  }
  
  /**
   * Draw arrow
   */
  arrow(x1: number, y1: number, x2: number, y2: number, headSize = 10): void {
    this.line(x1, y1, x2, y2);
    
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const headAngle = Math.PI / 6;
    
    const ax = x2 - Math.cos(angle - headAngle) * headSize;
    const ay = y2 - Math.sin(angle - headAngle) * headSize;
    const bx = x2 - Math.cos(angle + headAngle) * headSize;
    const by = y2 - Math.sin(angle + headAngle) * headSize;
    
    this.line(x2, y2, ax, ay);
    this.line(x2, y2, bx, by);
  }
  
  /**
   * Draw cross (X)
   */
  cross(cx: number, cy: number, size: number): void {
    const half = size / 2;
    this.line(cx - half, cy - half, cx + half, cy + half);
    this.line(cx - half, cy + half, cx + half, cy - half);
  }
  
  /**
   * Draw plus (+)
   */
  plus(cx: number, cy: number, size: number): void {
    const half = size / 2;
    this.line(cx - half, cy, cx + half, cy);
    this.line(cx, cy - half, cx, cy + half);
  }
  
  /**
   * Draw grid
   */
  grid(x: number, y: number, width: number, height: number, cellWidth: number, cellHeight: number): void {
    // Vertical lines
    for (let gx = x; gx <= x + width; gx += cellWidth) {
      this.line(gx, y, gx, y + height);
    }
    
    // Horizontal lines
    for (let gy = y; gy <= y + height; gy += cellHeight) {
      this.line(x, gy, x + width, gy);
    }
  }
  
  // ==========================================================================
  // INTERNAL
  // ==========================================================================
  
  private pushVertex(x: number, y: number): void {
    const i = this.vertexCount * 6;
    this.vertexData[i + 0] = x;
    this.vertexData[i + 1] = y;
    this.vertexData[i + 2] = this.colorR;
    this.vertexData[i + 3] = this.colorG;
    this.vertexData[i + 4] = this.colorB;
    this.vertexData[i + 5] = this.colorA;
    this.vertexCount++;
  }
  
  private pushTriangleVertex(x: number, y: number): void {
    const i = this.triangleCount * 6;
    this.triangleData[i + 0] = x;
    this.triangleData[i + 1] = y;
    this.triangleData[i + 2] = this.colorR;
    this.triangleData[i + 3] = this.colorG;
    this.triangleData[i + 4] = this.colorB;
    this.triangleData[i + 5] = this.colorA;
    this.triangleCount++;
  }
  
  private flush(): void {
    const { ctx } = this;
    
    // Flush triangles first (filled shapes render behind outlines)
    if (this.triangleCount > 0) {
      const triByteOffset = this.triBaseVertex * SHAPE_VERTEX_SIZE;
      // Use subarray() to avoid allocating new Float32Array wrapper objects
      const triangleDataToUpload = this.triangleData.subarray(0, this.triangleCount * 6);
      ctx.updateBuffer(this.triangleBuffer, triangleDataToUpload, triByteOffset);
      
      ctx.setPipeline(this.currentHdrTarget ? this.trianglePipelineHDR : this.trianglePipeline);
      ctx.setUniform('uViewProjection', this.vpMatrix);
      ctx.bindVertexBuffer(this.triangleBuffer);
      ctx.setupVertexAttributes(SHAPE_VERTEX_LAYOUT);
      ctx.draw(this.triangleCount, this.triBaseVertex);
      
      this.triBaseVertex += this.triangleCount;
      this.triangleCount = 0;
    }
    
    // Flush lines
    if (this.vertexCount > 0) {
      const lineByteOffset = this.lineBaseVertex * SHAPE_VERTEX_SIZE;
      // Use subarray() to avoid allocating new Float32Array wrapper objects
      const dataToUpload = this.vertexData.subarray(0, this.vertexCount * 6);
      ctx.updateBuffer(this.vertexBuffer, dataToUpload, lineByteOffset);
      
      ctx.setPipeline(this.currentHdrTarget ? this.pipelineHDR : this.pipeline);
      ctx.setUniform('uViewProjection', this.vpMatrix);
      ctx.bindVertexBuffer(this.vertexBuffer);
      ctx.setupVertexAttributes(SHAPE_VERTEX_LAYOUT);
      ctx.draw(this.vertexCount, this.lineBaseVertex);
      
      this.lineBaseVertex += this.vertexCount;
      this.vertexCount = 0;
    }
  }
  
  /**
   * Destroy resources
   */
  destroy(): void {
    if (this.vertexBuffer !== NULL_HANDLE) {
      this.ctx.destroyBuffer(this.vertexBuffer);
    }
    if (this.triangleBuffer !== NULL_HANDLE) {
      this.ctx.destroyBuffer(this.triangleBuffer);
    }
    if (this.pipeline !== NULL_HANDLE) {
      this.ctx.destroyPipeline(this.pipeline);
    }
    if (this.pipelineHDR !== NULL_HANDLE) {
      this.ctx.destroyPipeline(this.pipelineHDR);
    }
    if (this.trianglePipeline !== NULL_HANDLE) {
      this.ctx.destroyPipeline(this.trianglePipeline);
    }
    if (this.trianglePipelineHDR !== NULL_HANDLE) {
      this.ctx.destroyPipeline(this.trianglePipelineHDR);
    }
    if (this.shader !== NULL_HANDLE) {
      this.ctx.destroyShader(this.shader);
    }
  }
}
